import { useState } from "react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Clock,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  useGetQBOStatus,
  useDisconnectQBO,
  useGetQBOSyncLog,
  type QBOSyncLogEntry,
} from "@workspace/api-client-react";

function SyncStatusBadge({ status }: { status: string }) {
  if (status === "success") {
    return (
      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Success
      </Badge>
    );
  }
  if (status === "failure") {
    return (
      <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900">
        <AlertCircle className="w-3 h-3 mr-1" /> Failed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      <Clock className="w-3 h-3 mr-1" /> {status}
    </Badge>
  );
}

function entityLabel(entry: QBOSyncLogEntry): string {
  const t = entry.entityType;
  if (t === "invoice") return `Invoice #${entry.entityId ?? ""}`;
  if (t === "estimate" || t === "quote") return `Estimate #${entry.entityId ?? ""}`;
  return `${t}${entry.entityId ? ` #${entry.entityId}` : ""}`;
}

/**
 * Pull a human-readable note out of a sync_log row's payload_summary blob.
 * The push handler sets `note` (e.g. when an invoice synced without its
 * deposit because the QBO Sales Deposit preference is off) so the UI can
 * surface a warning beside an otherwise-successful row.
 */
function payloadNote(entry: QBOSyncLogEntry): string | null {
  if (!entry.payloadSummary) return null;
  try {
    const parsed = JSON.parse(entry.payloadSummary) as { note?: unknown };
    return typeof parsed.note === "string" && parsed.note.length > 0 ? parsed.note : null;
  } catch {
    return null;
  }
}

export default function QuickBooksSettingsPage() {
  usePageTitle("QuickBooks");
  const { toast } = useToast();

  const { data: status, isLoading: statusLoading } = useGetQBOStatus();
  const disconnect = useDisconnectQBO();
  const { data: syncLog, isLoading: logLoading } = useGetQBOSyncLog(50);

  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const onDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      toast({ title: "Disconnected", description: "QuickBooks has been disconnected." });
      setConfirmDisconnect(false);
    } catch (err) {
      toast({
        title: "Disconnect failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const onConnect = () => {
    window.location.href = "/api/integrations/quickbooks/connect";
  };

  if (statusLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const connected = status?.connected === true;
  const needsReconnect = connected && (status as { needsReconnect?: boolean }).needsReconnect;

  // Restrict the activity feed to invoice/estimate push entries only.
  const entries = (syncLog?.entries ?? []).filter(
    (e) => e.entityType === "invoice" || e.entityType === "estimate" || e.entityType === "quote",
  );

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link href="/integrations" className="inline-flex items-center gap-1 hover:text-foreground" data-testid="link-back-integrations">
          <ArrowLeft className="w-4 h-4" /> Back to integrations
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">QuickBooks</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Push invoices and estimates to your QuickBooks Online company on demand.
          </p>
        </div>
        {connected ? (
          <div className="flex items-center gap-2">
            {needsReconnect ? (
              <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900">
                <AlertCircle className="w-3 h-3 mr-1" /> Reconnect needed
              </Badge>
            ) : (
              <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
              </Badge>
            )}
          </div>
        ) : null}
      </div>

      {!connected ? (
        <Card>
          <CardHeader>
            <CardTitle>Connect QuickBooks Online</CardTitle>
            <CardDescription>
              Authorize QuoTrak to send invoices and estimates to your QuickBooks Online company.
              Nothing syncs automatically — you push each document yourself from its detail page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={onConnect} disabled={status?.configured === false} data-testid="button-qbo-connect">
              Connect to QuickBooks
            </Button>
            {status?.configured === false ? (
              <p className="mt-2 text-xs text-muted-foreground">
                QuickBooks integration is not configured on this server.
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Connection</CardTitle>
              <CardDescription>
                Connected to{" "}
                <span className="font-medium text-foreground" data-testid="text-qbo-company">
                  {(status as { companyName?: string }).companyName ?? "your QuickBooks company"}
                </span>
                {connected && (status as { connectedAt?: string }).connectedAt ? (
                  <> since {new Date((status as { connectedAt: string }).connectedAt).toLocaleDateString()}</>
                ) : null}
                .
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {needsReconnect ? (
                <div className="flex items-start gap-2 text-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200 rounded-md p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Your QuickBooks token is no longer valid. Reconnect to continue pushing
                    documents.
                    <div className="mt-2">
                      <Button size="sm" onClick={onConnect} data-testid="button-qbo-reconnect">
                        Reconnect
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}

              <Button
                variant="outline"
                onClick={() => setConfirmDisconnect(true)}
                data-testid="button-qbo-disconnect"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How it works</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
                <li>Open any invoice or estimate and click <span className="font-medium text-foreground">Push to QuickBooks</span>.</li>
                <li>The first push creates the document in QuickBooks; later pushes update it in place.</li>
                <li>Customers, products, and tax rates are not synced — QuoTrak remains the source of truth.</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent push activity</CardTitle>
              <CardDescription>The last 50 invoice and estimate pushes from QuoTrak to QuickBooks.</CardDescription>
            </CardHeader>
            <CardContent>
              {logLoading ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : entries.length === 0 ? (
                <p className="text-sm text-muted-foreground">No push activity yet.</p>
              ) : (
                <div className="divide-y">
                  {entries.map((e) => {
                    const note = e.status === "success" ? payloadNote(e) : null;
                    return (
                      <div key={e.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <div className="font-medium truncate" data-testid={`row-qbo-log-${e.id}`}>
                            {entityLabel(e)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(e.createdAt).toLocaleString()}
                            {e.errorMessage ? <> · {e.errorMessage}</> : null}
                          </div>
                          {note ? (
                            <div
                              className="text-xs text-amber-700 dark:text-amber-400 mt-0.5"
                              data-testid={`text-qbo-log-note-${e.id}`}
                            >
                              {note}
                            </div>
                          ) : null}
                        </div>
                        <SyncStatusBadge status={e.status} />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect QuickBooks?</DialogTitle>
            <DialogDescription>
              You can reconnect at any time. Existing invoices and estimates already in QuickBooks
              will not be deleted, but you won't be able to push updates to them until you
              reconnect.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisconnect(false)} data-testid="button-disconnect-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDisconnect} disabled={disconnect.isPending} data-testid="button-disconnect-confirm">
              {disconnect.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
